import React, { useState, useEffect, useCallback } from 'react';

/**
 * Reminders tab for the Care dashboard. List + add + pause/resume/delete
 * for one managed account. Ported from the old RemindersModal; uses the
 * shared .care-* classes and surfaces auth/network errors instead of
 * silently showing an empty list.
 */
export default function CareRemindersTab({ account, token, api }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const _defaultLocal = () => {
    const d = new Date(Date.now() + 10 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [fireAt, setFireAt] = useState(_defaultLocal());
  const [recurring, setRecurring] = useState('');

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${api}/api/managed-accounts/${encodeURIComponent(account.id)}/reminders`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setReminders(data.reminders || []);
    } catch (e) {
      setError(e.message);
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, [api, token, account.id]);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const submitNew = async (e) => {
    e?.preventDefault?.();
    if (!title.trim() || !content.trim() || !fireAt) return;
    setAdding(true);
    setError('');
    try {
      const fireAtIso = new Date(fireAt).toISOString();
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await fetch(
        `${api}/api/managed-accounts/${encodeURIComponent(account.id)}/reminders`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ title: title.trim(), content: content.trim(), fire_at: fireAtIso, recurring: recurring || null, timezone: tz }),
        },
      );
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `HTTP ${res.status}`); }
      setTitle(''); setContent(''); setRecurring(''); setFireAt(_defaultLocal());
      await fetchReminders();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const togglePause = async (r) => {
    const verb = r.status === 'paused' ? 'resume' : 'pause';
    try {
      const res = await fetch(
        `${api}/api/managed-accounts/${encodeURIComponent(account.id)}/reminders/${r.id}/${verb}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchReminders();
    } catch (e) { setError(e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete reminder "${r.title}"?`)) return;
    try {
      const res = await fetch(
        `${api}/api/managed-accounts/${encodeURIComponent(account.id)}/reminders/${r.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchReminders();
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <form onSubmit={submitNew} style={{ marginBottom: 18 }}>
        <label className="care-field">
          <div className="care-field-label">Title</div>
          <input className="care-input" placeholder="e.g. Morning medication" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="care-field">
          <div className="care-field-label">What should the companion say?</div>
          <textarea className="care-textarea" rows={2} placeholder="Time to take your blood pressure pill." value={content} onChange={(e) => setContent(e.target.value)} />
        </label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input className="care-input" type="datetime-local" value={fireAt} onChange={(e) => setFireAt(e.target.value)} style={{ flex: 1 }} aria-label="Reminder time" />
          <select className="care-select" value={recurring} onChange={(e) => setRecurring(e.target.value)} style={{ width: 140 }} aria-label="Repeat">
            <option value="">One-time</option>
            <option value="daily">Daily</option>
            <option value="weekdays">Weekdays</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <button type="submit" className="care-btn primary" disabled={adding || !title.trim() || !content.trim() || !fireAt}>
          {adding ? 'Adding…' : '+ Add reminder'}
        </button>
      </form>

      {error && <div className="care-error-state" role="alert">{error}</div>}

      {loading ? (
        <><div className="care-skeleton" /><div className="care-skeleton" /></>
      ) : reminders.length === 0 ? (
        <div className="care-empty-state">No reminders yet. Add one above.</div>
      ) : (
        reminders.map((r) => (
          <div key={r.id} className="care-list-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="care-row-title">
                {r.title}
                {r.status === 'paused' && <span style={{ marginLeft: 8, fontSize: 11, color: '#fbbf24' }}>(paused)</span>}
                {r.status === 'done' && <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>(done)</span>}
              </div>
              <div className="care-row-meta">
                {(() => {
                  const when = r.next_fire_at ? new Date(r.next_fire_at).toLocaleString() : '—';
                  return r.recurring ? `${r.recurring} · next ${when}` : `one-time · ${when}`;
                })()}
              </div>
              <div className="care-row-body">{r.content}</div>
            </div>
            <div className="care-row-actions">
              {r.status !== 'done' && (
                <button className="care-mini-btn" onClick={() => togglePause(r)} aria-label={r.status === 'paused' ? 'Resume reminder' : 'Pause reminder'}>
                  {r.status === 'paused' ? 'Resume' : 'Pause'}
                </button>
              )}
              <button className="care-mini-btn danger" onClick={() => remove(r)} aria-label="Delete reminder">Delete</button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
